import React, {
  useState,
  useRef,
  useImperativeHandle,
  useEffect,
  forwardRef,
  useMemo,
} from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Alignment,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  RemoveFormat,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
} from "ckeditor5";

import './Settings.css'
import "ckeditor5/ckeditor5.css";
import { useDispatch } from "react-redux";
import { currentSelectedLanguage, updateSettingsData } from "../../../StateManagement/actions";
import { useSelector } from "react-redux";

const LICENSE_KEY = "GPL";


const TermsOfService = forwardRef(
  (
    { content = "", onUserEdit, isReadOnly = false, isDisable = false },
    ref
  ) => {
    const selectedLanguage=useSelector(currentSelectedLanguage);
    const dispatch = useDispatch();
    const [editorData, setEditorData] = useState(selectedLanguage.LstLanguageCodes.UI_ByContinuingTheProcess);
    const editorRef = useRef(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const outerTabSelected = useSelector(state => state.settings.settingFor);

    const trimHtmlContent = (html) =>
      (html || "")
        .replace(/(<p[^>]*>(<[^>]+>|&nbsp;|\s)*<\/p>)+$/gi, "")
        .replace(/(&nbsp;|\s)+(?=<\/[^>]+>)/gi, "")
        .trim();

    // Sync editor content with external changes
    useEffect(() => {
      if (content && content !== editorData) {
        setEditorData(content);
        if (editorRef.current) {
          editorRef.current.setData(content);
        }
      }
      else if(content ==='' && isDisable) {
        setEditorData(selectedLanguage.LstLanguageCodes.UI_ByContinuingTheProcess);

      }
    }, [content]);

    useEffect(() => {
      setIsLayoutReady(true);
      return () => setIsLayoutReady(false);
    }, []);

    useEffect(() => {
      return () => {
        if (editorRef.current) {
          editorRef.current.destroy();
        }
      };
    }, []);

    useImperativeHandle(ref, () => ({
      getContent: () => editorData,
      focus: () => {
        if (editorRef.current) {
          editorRef.current.editing.view.focus();
        }
      },
    }));

    const handleBlur = (event, editor) => {
      const rawData = editor.getData();
      const trimmed = trimHtmlContent(rawData);
      setEditorData(trimmed);
      dispatch(
        updateSettingsData({
          type: "TermsOfService",
          value: trimmed,
          outerTabSelected: outerTabSelected === "User" ? "Personal" : outerTabSelected,
          level: 'common',
        })
      );
    };

    const handleChange = (event, editor) => {
      setEditorData(editor.getData());
    };

    const { editorConfig } = useMemo(() => {
      if (!isLayoutReady) return {};

      return {
        editorConfig: {
          toolbar: {
            items: [
              "heading",
              "bold",
              "italic",
              "underline",
              "numberedList",
              "bulletedList",
              "link",
              "alignment",
              "undo",
              "redo",
            ],
            shouldNotGroupWhenFull: true,
          },
          plugins: [
            Alignment,
            Autosave,
            BlockQuote,
            Bold,
            Code,
            CodeBlock,
            Essentials,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            Heading,
            Highlight,
            HorizontalLine,
            Indent,
            IndentBlock,
            Italic,
            Link,
            List,
            ListProperties,
            Paragraph,
            RemoveFormat,
            Strikethrough,
            Subscript,
            Superscript,
            Underline,
          ],
          fontFamily: { supportAllValues: true },
          fontSize: {
            options: [10, 12, 14, "default", 18, 20, 22],
            supportAllValues: true,
          },
          heading: {
            options: [
              {
                model: "paragraph",
                title: "Paragraph",
                class: "ck-heading_paragraph",
              },
              {
                model: "heading1",
                view: "h1",
                title: "Heading 1",
                class: "ck-heading_heading1",
              },
              {
                model: "heading2",
                view: "h2",
                title: "Heading 2",
                class: "ck-heading_heading2",
              },
              {
                model: "heading3",
                view: "h3",
                title: "Heading 3",
                class: "ck-heading_heading3",
              }
              
            ],
          },
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: "https://"
          },
          licenseKey: LICENSE_KEY,
        },
      };
    }, [isLayoutReady]);

    return (
      <div
        className="main-container"
        style={{
          opacity: isDisable ? 0.5 : 1,
          pointerEvents: isDisable ? "none" : "auto",
        }}>
        <div className="editor-container editor-container_classic-editor">
          <div className="editor-container__editor">
            {editorConfig && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={editorData}
                disabled={isReadOnly}
                onReady={(editor) => {
                  editorRef.current = editor;
                }}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default TermsOfService;
